import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { CloudUpload, Close } from "@mui/icons-material";
import { axiosInstance } from "../../../../../utils/axios";
import { API_URL } from "../../../../../providers/dataProvider";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { useHandleNotification, useInvalidate } from "@refinedev/core";

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ExcelImportDialog: React.FC<ExcelImportDialogProps> = ({
  open,
  onClose,
}) => {
  const invalidate = useInvalidate();
  const handleNotification = useHandleNotification();
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.shipmentPrices.details.importDialog"
  );

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file!);

      const response = await axiosInstance.post(
        API_URL + "/shipment-prices/import",
        formData
      );

      if (!response?.status || response?.status !== 200) {
        throw new Error(response.data?.message ?? translate("errorMessage"));
      }

      setUploading(false);
      setFile(null);

      handleNotification({
        message: translate("successMessage"),
        type: "success",
      });

      invalidate({
        resource: "all",
        invalidates: ["all"],
      });

      onClose();
    } catch (err) {
      setUploading(false);
      setError(err instanceof Error ? err.message : translate("errorMessage"));
    }
  };

  const handleDialogClose = () => {
    setFile(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {translate("title")}
        <IconButton aria-label="close" onClick={handleDialogClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {translate("description")}
        </Typography>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: "2px dashed #90caf9",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#f5fafd",
            mb: 2,
          }}
          onClick={() => inputRef.current?.click()}
        >
          <CloudUpload sx={{ fontSize: 48, color: "#90caf9" }} />
          <Typography variant="body1" sx={{ mt: 1 }}>
            {translate("dragAndDrop")}
          </Typography>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {translate("selectedFile", { fileName: file.name })}
            </Typography>
          )}
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={uploading}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!file || uploading}
          startIcon={
            uploading ? <CircularProgress size={20} /> : <CloudUpload />
          }
        >
          {uploading ? translate("uploading") : translate("uploadButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
